import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Volume2, ClipboardCheck, ShieldQuestion, X, Sparkles } from "lucide-react";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { VoiceWaveform } from "@/components/VoiceWaveform";
import { VoiceCall } from "@/components/VoiceCall";
import { Portal } from "@/components/Portal";
import { useStore } from "@/lib/store";
import { METRICS } from "@/data/seed";

// Ava's dismissible morning briefing shown on first HQ entry.
export function MorningBriefing({ onClose, onTalk }) {
  const navigate = useNavigate();
  const { state, setBriefingSeen } = useStore();
  const ava = state.employees.find((e) => e.id === "ava");
  const [speaking, setSpeaking] = useState(false);
  const [call, setCall] = useState(false);

  const dismiss = () => { setBriefingSeen(); onClose(); };

  const briefingScript =
    "Greet Lewis warmly by name, then deliver this morning briefing out loud in your own voice, naturally: " +
    "'Good morning, Lewis. Your AI team completed 347 tasks this month. Eleven AEDs require attention today, " +
    "and two decisions are waiting for your approval.' Then ask if he'd like you to walk through the exceptions or open the Approval Center.";

  if (call) {
    return <VoiceCall employee={ava} greet={briefingScript} onClose={() => setCall(false)} />;
  }

  return (
    <Portal>
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[60] grid place-items-center p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        data-testid="morning-briefing"
      >
        <div className="absolute inset-0 bg-[#030A16]/80 backdrop-blur-sm" onClick={dismiss} />
        <motion.div
          className="glass-strong relative w-full max-w-lg overflow-hidden rounded-2xl p-7"
          initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, opacity: 0 }}
          transition={{ type: "spring", stiffness: 220, damping: 22 }}
        >
          <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full" style={{ background: "radial-gradient(circle, rgba(0,229,255,0.25), transparent 70%)" }} />
          <button onClick={dismiss} className="absolute right-4 top-4 text-slate-400 hover:text-white" aria-label="Dismiss briefing" data-testid="briefing-dismiss-x"><X size={18} /></button>

          <div className="flex items-center gap-4">
            <motion.div animate={speaking ? { scale: [1, 1.04, 1] } : {}} transition={{ duration: 0.8, repeat: speaking ? Infinity : 0 }}>
              <EmployeeAvatar employee={ava} size={68} showStatus={false} />
            </motion.div>
            <div>
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wide text-[#00E5FF]"><Sparkles size={13} /> Morning briefing</div>
              <div className="font-display text-xl font-semibold text-white">Ava · Administration Assistant</div>
            </div>
          </div>

          <p className="mt-5 text-[15px] leading-relaxed text-slate-200">
            Good morning, Lewis. Your AI team completed <span className="text-white font-semibold">{METRICS.tasksMonth} tasks</span> this month.
            <span className="text-white font-semibold"> Eleven AEDs</span> require attention today, and
            <span className="text-white font-semibold"> two decisions</span> are waiting for your approval.
          </p>

          <div className="mt-5 rounded-xl border border-white/10 bg-[#030A16]/50 p-3">
            <VoiceWaveform active={speaking} height={40} />
            <div className="mt-1 text-center text-[11px] font-mono text-slate-500">
              {speaking ? "Ava is speaking… (simulated voice)" : "Voice playback is simulated in this prototype"}
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-2.5">
            <button
              onClick={() => setCall(true)}
              className="col-span-2 flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] px-4 py-2.5 text-sm font-semibold text-[#030A16] transition-transform hover:-translate-y-0.5"
              data-testid="briefing-start-spoken"
            >
              <Volume2 size={16} /> Start Spoken Briefing
            </button>
            <button onClick={() => { setBriefingSeen(); onClose(); navigate("/mission"); }} className="flex items-center justify-center gap-2 rounded-lg border border-white/10 px-3 py-2.5 text-sm text-slate-200 hover:border-[#00E5FF]/40 hover:text-white" data-testid="briefing-review-exceptions">
              <ClipboardCheck size={15} /> Review Exceptions
            </button>
            <button onClick={() => { setBriefingSeen(); onClose(); navigate("/approvals"); }} className="flex items-center justify-center gap-2 rounded-lg border border-white/10 px-3 py-2.5 text-sm text-slate-200 hover:border-[#FFBF00]/40 hover:text-white" data-testid="briefing-open-approvals">
              <ShieldQuestion size={15} /> Open Approval Center
            </button>
            <button onClick={dismiss} className="col-span-2 rounded-lg px-3 py-2 text-sm text-slate-400 hover:text-white" data-testid="briefing-dismiss">
              Dismiss
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
    </Portal>
  );
}
